import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';

export interface DisclosureProps {
  summary: ReactNode;
  /** Right-hand side of the trigger on a desk, under the summary on a phone: a Badge, a count, a date. */
  meta?: ReactNode;
  defaultOpen?: boolean;
  /** Drive it from outside when several sections open together. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children: ReactNode;
}

/**
 * A hairline row with a mono + / − that is a real button with aria-expanded.
 * The glyph is aria-hidden so it is not read aloud as 'plus'.
 *
 * The meta used to be `shrink-0` beside a summary that was allowed to
 * shrink to nothing, so on a 360px phone call detail read "Call settings"
 * one letter per line next to "+1 province · 5 criteria · envelope
 * R 5 000 000,00". The summary now keeps a floor of 8rem and the meta
 * drops under it below 640px, lined up with the summary text, and the
 * panel gives back its 28px indent on a phone where every pixel of a
 * form's width counts.
 */
export function Disclosure({
  summary,
  meta,
  defaultOpen = false,
  open,
  onOpenChange,
  className,
  children,
}: DisclosureProps) {
  const [internal, setInternal] = useState(defaultOpen);
  const isOpen = open ?? internal;
  const panelId = useId();

  const toggle = () => {
    const next = !isOpen;
    if (open === undefined) setInternal(next);
    onOpenChange?.(next);
  };

  return (
    <div className={cx('border-b border-line', className)}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
        className={cx(
          'flex w-full min-h-tap flex-wrap items-center justify-between gap-x-3 gap-y-0.5 py-2 text-left',
          'text-sm font-semibold text-ink transition-colors duration-state ease-ct hover:bg-forest-whisper active:bg-forest-whisper',
          focusRing,
        )}
      >
        <span className="flex min-w-[8rem] flex-1 items-center gap-3">
          <span aria-hidden="true" className="record w-4 shrink-0 text-center text-sm text-ink-3">
            {isOpen ? '−' : '+'}
          </span>
          <span className="min-w-0 [overflow-wrap:break-word]">{summary}</span>
        </span>
        {meta ? (
          <span className="min-w-0 basis-full pl-7 text-xs font-normal text-ink-3 sm:basis-auto sm:pl-0">{meta}</span>
        ) : null}
      </button>
      <div id={panelId} hidden={!isOpen} className="pb-3 pl-0 text-sm text-ink-2 sm:pl-7">
        {children}
      </div>
    </div>
  );
}
